import React from 'react'
import { motion } from 'framer-motion'
import { Users, Search, Filter, Star, MapPin, Music, Grid, List } from 'lucide-react'
import ArtistCard from '../components/ui/ArtistCard'
import FilterPanel from '../components/ui/FilterPanel'
import { Artist } from '../types'

const ArtistsPage = () => {
  const [view, setView] = React.useState<'grid' | 'list'>('grid')
  const [filters, setFilters] = React.useState<any>({})
  const [isFilterOpen, setIsFilterOpen] = React.useState(false)

  const mockArtists: Artist[] = [
    {
      id: '1',
      name: 'Sarah Mitchell',
      bio: 'Wichita-born singer-songwriter with a soulful voice and heartfelt lyrics that capture the essence of Midwest life.',
      genres: ['Indie Folk', 'Acoustic'],
      image: 'https://images.pexels.com/photos/1587927/pexels-photo-1587927.jpeg?auto=compress&cs=tinysrgb&w=800',
      followers: 1234,
      rating: 4.9,
      verified: true,
      upcomingShows: 3,
      location: 'Wichita, KS',
      socials: {
        website: 'https://sarahmitchellmusic.com',
        spotify: 'https://spotify.com/sarahmitchell',
        instagram: 'https://instagram.com/sarahmitchellmusic'
      },
      bookingInfo: {
        available: true,
        rate: '$500-1000/show',
        contact: 'booking@sarahmitchellmusic.com'
      }
    },
    {
      id: '2',
      name: 'Thunder Road',
      bio: 'High-energy rock band bringing classic rock vibes with a modern twist to stages across Kansas.',
      genres: ['Classic Rock', 'Hard Rock'],
      image: 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg?auto=compress&cs=tinysrgb&w=800',
      followers: 892,
      rating: 4.7,
      verified: true,
      upcomingShows: 2,
      location: 'Wichita, KS',
      bookingInfo: {
        available: true,
        rate: '$800-1500/show'
      }
    },
    {
      id: '3',
      name: 'Wichita Jazz Collective',
      bio: 'Smooth jazz ensemble featuring some of Wichita\'s finest musicians, perfect for intimate venues.',
      genres: ['Jazz', 'Smooth Jazz'],
      image: 'https://images.pexels.com/photos/1540406/pexels-photo-1540406.jpeg?auto=compress&cs=tinysrgb&w=800',
      followers: 567,
      rating: 4.8,
      verified: false,
      upcomingShows: 4,
      location: 'Wichita, KS',
      bookingInfo: {
        available: true,
        rate: '$600-1200/show'
      }
    },
    {
      id: '4',
      name: 'Electric Dreams',
      bio: 'Cutting-edge electronic music producer and live performer pushing the boundaries of digital sound.',
      genres: ['Electronic', 'Synthwave'],
      image: 'https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg?auto=compress&cs=tinysrgb&w=800',
      followers: 2156,
      rating: 4.6,
      verified: true,
      upcomingShows: 1,
      location: 'Wichita, KS',
      bookingInfo: {
        available: false
      }
    }
  ]

  return (
    <div className="pt-16 lg:pt-20 min-h-screen">
      {/* Header */}
      <section className="section-padding py-12 bg-gradient-to-b from-le-darker/50 to-transparent">
        <div className="container-max">
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl lg:text-6xl font-display font-bold text-white mb-4">
              Local
              <span className="text-gradient block">Artists</span>
            </h1>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              Discover the incredible talent that makes Wichita's music scene so vibrant.
            </p>
          </motion.div>

          {/* Search and Controls */}
          <motion.div
            className="glass-card p-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="flex flex-col lg:flex-row gap-4 items-center">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" size={20} />
                <input
                  type="text"
                  placeholder="Search artists, bands, genres..."
                  className="w-full bg-white/10 border border-white/20 rounded-xl pl-10 pr-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-le-accent transition-colors duration-200"
                />
              </div>

              {/* View Toggle */}
              <div className="flex bg-white/10 rounded-xl p-1">
                {[
                  { key: 'grid', icon: Grid, label: 'Grid' },
                  { key: 'list', icon: List, label: 'List' }
                ].map(({ key, icon: Icon, label }) => (
                  <button
                    key={key}
                    onClick={() => setView(key as any)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                      view === key
                        ? 'bg-le-accent text-white'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Icon size={18} />
                    <span className="hidden sm:inline">{label}</span>
                  </button>
                ))}
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <button 
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <Filter size={18} />
                  <span>Filters</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding pb-20">
        <div className="container-max flex gap-8">
          {/* Main Content */}
          <div className="flex-1">
            <div className={`grid gap-6 ${
              view === 'grid' 
                ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
                : 'grid-cols-1'
            }`}>
              {mockArtists.map((artist, index) => (
                <motion.div
                  key={artist.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <ArtistCard artist={artist} size={view === 'list' ? 'large' : 'medium'} />
                </motion.div>
              ))}
            </div>
          </div>

          {/* Filter Sidebar */}
          <div className="hidden lg:block w-80">
            <FilterPanel
              isOpen={true}
              onClose={() => {}}
              filters={filters}
              onFiltersChange={setFilters}
              type="artists"
            />
          </div>
        </div>
      </section>

      {/* Mobile Filter Panel */}
      <FilterPanel
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        filters={filters}
        onFiltersChange={setFilters}
        type="artists"
      />
    </div>
  )
}

export default ArtistsPage